// ARQUIVO ATUALIZADO
import React, { useState, useMemo, useEffect } from 'react';
import FlowBoard from './FlowBoard';
import ListView from './ListView';
import DesignCalendar from './DesignCalendar';
import useData from '../hooks/useData';
import { KanbanIcon, ListIcon, CalendarIcon, ChevronDownIcon } from '../components/ui/Icons';
import DropdownMenu from '../components/ui/DropdownMenu';

const FlowPage = ({ onTaskClick, onNewTaskClick }) => {
    const [activeView, setActiveView] = useState('flow');
    const { tasks, clients, projects } = useData();

    const [selectedClient, setSelectedClient] = useState('all');
    const [selectedProject, setSelectedProject] = useState('all');

    // --- INÍCIO DA ALTERAÇÃO 1: Efeito para resetar o filtro de projeto ao mudar o cliente ---
    useEffect(() => {
        setSelectedProject('all');
    }, [selectedClient]);
    // --- FIM DA ALTERAÇÃO 1 ---

    const filteredTasks = useMemo(() => {
        return tasks
            .filter(task => !task.isArchived)
            .filter(task => selectedClient === 'all' || task.client === selectedClient)
            .filter(task => selectedProject === 'all' || task.projectId === selectedProject);
    }, [tasks, selectedClient, selectedProject]);

    const renderActiveView = () => {
        switch (activeView) {
            case 'flow':
                return <FlowBoard tasks={filteredTasks} onTaskClick={onTaskClick} onNewTaskClick={onNewTaskClick} />;
            case 'list':
                return <ListView tasks={filteredTasks} onTaskClick={onTaskClick} />;
            case 'calendar':
                return <DesignCalendar tasks={filteredTasks} onTaskClick={onTaskClick} onNewTaskClick={onNewTaskClick} />;
            default:
                return <FlowBoard tasks={filteredTasks} onTaskClick={onTaskClick} onNewTaskClick={onNewTaskClick} />;
        }
    };

    const clientOptions = useMemo(() => {
        const options = [{ label: 'Todos os Clientes', value: 'all' }];
        clients.forEach(client => options.push({ label: client.name, value: client.name }));
        return options;
    }, [clients]);

    // --- INÍCIO DA ALTERAÇÃO 2: Lógica do filtro de projetos agora depende do cliente selecionado ---
    const projectOptions = useMemo(() => {
        const options = [{ label: 'Todos os Projetos', value: 'all' }];
        
        let projectsToShow = projects;

        if (selectedClient !== 'all') {
            const clientObj = clients.find(c => c.name === selectedClient);
            if (clientObj) {
                projectsToShow = projects.filter(p => p.clientId === clientObj.id);
            } else {
                projectsToShow = []; 
            }
        }

        projectsToShow.forEach(project => {
            options.push({ label: project.name, value: project.id });
        });

        return options;
    }, [projects, clients, selectedClient]);
    // --- FIM DA ALTERAÇÃO 2 ---

    const getClientLabel = (value) => {
        if (value === 'all') return 'Todos os Clientes';
        return clients.find(c => c.name === value)?.name || value;
    };

    const getProjectLabel = (value) => {
        if (value === 'all') return 'Todos os Projetos';
        return projects.find(p => p.id === value)?.name || 'Todos os Projetos';
    };

    return (
        <div>
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 px-4 gap-4">
                <div className="flex gap-1 rounded-lg bg-black/20 p-1">
                    <button
                        onClick={() => setActiveView('flow')}
                        className={`flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${activeView === 'flow' ? 'bg-indigo-500' : 'hover:bg-white/10'}`}
                    >
                        <KanbanIcon size={16} />
                        <span>Fluxo</span>
                    </button>
                    <button
                        onClick={() => setActiveView('list')}
                        className={`flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${activeView === 'list' ? 'bg-indigo-500' : 'hover:bg-white/10'}`}
                    >
                        <ListIcon size={16} />
                        <span>Lista</span>
                    </button>
                    <button
                        onClick={() => setActiveView('calendar')}
                        className={`flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${activeView === 'calendar' ? 'bg-indigo-500' : 'hover:bg-white/10'}`}
                    >
                        <CalendarIcon size={16} />
                        <span>Agenda</span>
                    </button>
                </div>

                <div className="flex items-center gap-4">
                    <DropdownMenu
                        trigger={
                            <button className="flex items-center justify-between w-48 bg-white/10 text-white text-sm rounded-lg py-2 px-3 border border-transparent hover:border-white/20 transition-colors">
                                <span className="truncate">{getClientLabel(selectedClient)}</span>
                                <ChevronDownIcon size={20} className="ml-2 text-gray-400" />
                            </button>
                        }
                    >
                        <div className="max-h-60 w-48 overflow-y-auto">
                            {clientOptions.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => setSelectedClient(option.value)}
                                    className="w-full text-left px-3 py-2 text-sm text-white hover:bg-indigo-600"
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </DropdownMenu>
                    
                     <DropdownMenu
                        trigger={
                            <button className="flex items-center justify-between w-48 bg-white/10 text-white text-sm rounded-lg py-2 px-3 border border-transparent hover:border-white/20 transition-colors">
                                <span className="truncate">{getProjectLabel(selectedProject)}</span>
                                <ChevronDownIcon size={20} className="ml-2 text-gray-400" />
                            </button>
                        }
                    >
                        <div className="max-h-60 w-48 overflow-y-auto">
                            {projectOptions.map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => setSelectedProject(option.value)}
                                    className="w-full text-left px-3 py-2 text-sm text-white hover:bg-indigo-600"
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    </DropdownMenu>
                </div>
            </div>

            {renderActiveView()}
        </div>
    );
};

export default FlowPage;